import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SlidersHorizontalIcon,
  XIcon,
} from 'lucide-react'
import { MobileFilterBar, MobileSelect } from './mobile'
import {
  isDateRangeActive,
  toggleMultiSelectValue,
  type DateRange,
  type FilterOption,
  type SortDir,
  type SortOption,
} from '../lib/listFilterSort'

export interface ListFilterControl {
  type?: 'select'
  /** Short label shown in the filter panel and active chips */
  label?: string
  value: string
  onChange: (value: string) => void
  options: FilterOption[]
  /** Accessible name for the select */
  ariaLabel?: string
  className?: string
  /** Option value meaning "no filter" (default 'all') */
  allValue?: string
}

export interface MultiListFilterControl {
  type: 'multi'
  label: string
  value: string[]
  onChange: (value: string[]) => void
  options: FilterOption[]
}

export interface DateRangeListFilterControl {
  type: 'daterange'
  label: string
  value: DateRange
  onChange: (value: DateRange) => void
}

export type ListFilter =
  | ListFilterControl
  | MultiListFilterControl
  | DateRangeListFilterControl

interface ListFilterSortBarProps {
  search: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string
  filters?: ListFilter[]
  sortValue: string
  onSortChange: (value: string) => void
  sortOptions: SortOption[]
  sortDir: SortDir
  onSortDirChange: (dir: SortDir) => void
  trailing?: React.ReactNode
  className?: string
  children?: React.ReactNode
}

interface ActiveChip {
  key: string
  label: string
  onClear: () => void
}

function selectAllValue(filter: ListFilterControl): string {
  return filter.allValue ?? 'all'
}

function optionLabel(options: FilterOption[], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value
}

function formatRangeChip(range: DateRange): string {
  const from = range.from || '…'
  const to = range.to || '…'
  return `${from} – ${to}`
}

function buildActiveChips(filters: ListFilter[]): ActiveChip[] {
  const chips: ActiveChip[] = []
  filters.forEach((filter, index) => {
    if (filter.type === 'multi') {
      for (const value of filter.value) {
        chips.push({
          key: `f${index}-${value}`,
          label: `${filter.label}: ${optionLabel(filter.options, value)}`,
          onClear: () => filter.onChange(toggleMultiSelectValue(filter.value, value)),
        })
      }
    } else if (filter.type === 'daterange') {
      if (isDateRangeActive(filter.value)) {
        chips.push({
          key: `f${index}-range`,
          label: `${filter.label}: ${formatRangeChip(filter.value)}`,
          onClear: () => filter.onChange({ from: '', to: '' }),
        })
      }
    } else {
      const allValue = selectAllValue(filter)
      if (filter.value !== allValue) {
        const prefix = filter.label ? `${filter.label}: ` : ''
        chips.push({
          key: `f${index}-${filter.value}`,
          label: `${prefix}${optionLabel(filter.options, filter.value)}`,
          onClear: () => filter.onChange(allValue),
        })
      }
    }
  })
  return chips
}

function clearAllFilters(filters: ListFilter[]) {
  for (const filter of filters) {
    if (filter.type === 'multi') {
      if (filter.value.length > 0) filter.onChange([])
    } else if (filter.type === 'daterange') {
      if (isDateRangeActive(filter.value)) filter.onChange({ from: '', to: '' })
    } else {
      const allValue = selectAllValue(filter)
      if (filter.value !== allValue) filter.onChange(allValue)
    }
  }
}

const panelLabelCls = 'block text-xs font-medium text-gray-600 mb-1'
const dateInputCls =
  'min-h-[40px] w-full min-w-0 px-2.5 py-1.5 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

function FilterPanelControl({ filter }: { filter: ListFilter }) {
  const { t } = useTranslation('common')
  if (filter.type === 'multi') {
    return (
      <div>
        <span className={panelLabelCls}>{filter.label}</span>
        <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100 bg-white">
          {filter.options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">{t('list.noOptions')}</div>
          ) : (
            filter.options.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                  checked={filter.value.includes(opt.value)}
                  onChange={() =>
                    filter.onChange(toggleMultiSelectValue(filter.value, opt.value))
                  }
                />
                <span className="truncate">{opt.label}</span>
              </label>
            ))
          )}
        </div>
      </div>
    )
  }

  if (filter.type === 'daterange') {
    return (
      <div>
        <span className={panelLabelCls}>{filter.label}</span>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={filter.value.from}
            onChange={(e) => filter.onChange({ ...filter.value, from: e.target.value })}
            aria-label={`${filter.label} ${t('list.from')}`}
            className={dateInputCls}
          />
          <span className="text-gray-400 text-sm flex-shrink-0">{t('list.to')}</span>
          <input
            type="date"
            value={filter.value.to}
            onChange={(e) => filter.onChange({ ...filter.value, to: e.target.value })}
            aria-label={`${filter.label} ${t('list.to')}`}
            className={dateInputCls}
          />
        </div>
      </div>
    )
  }

  return (
    <div>
      {filter.label && <span className={panelLabelCls}>{filter.label}</span>}
      <MobileSelect
        value={filter.value}
        onChange={(e) => filter.onChange(e.target.value)}
        aria-label={filter.ariaLabel || filter.label || t('list.filter')}
        className={`w-full ${filter.className || ''}`}
      >
        {filter.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </MobileSelect>
    </div>
  )
}

/**
 * Consistent search + filters + sort controls for list pages.
 * Filters live in a collapsible panel behind a "Filters" button; active filters
 * render as one-tap-clear chips with a "Clear all" action. The sort control is a
 * compact select + direction toggle (44px targets on mobile, slim on desktop).
 */
export function ListFilterSortBar({
  search,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters = [],
  sortValue,
  onSortChange,
  sortOptions,
  sortDir,
  onSortDirChange,
  trailing,
  className = '',
  children,
}: ListFilterSortBarProps) {
  const { t } = useTranslation('common')
  const [panelOpen, setPanelOpen] = useState(false)
  const chips = buildActiveChips(filters)

  return (
    <MobileFilterBar
      search={search}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchPlaceholder}
      className={className}
    >
      {filters.length > 0 && (
        <button
          type="button"
          onClick={() => setPanelOpen((open) => !open)}
          aria-expanded={panelOpen}
          className={`inline-flex items-center gap-1.5 min-h-[44px] lg:min-h-0 lg:h-9 px-3 rounded-xl border text-sm font-medium transition-colors ${
            panelOpen || chips.length > 0
              ? 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontalIcon className="w-4 h-4" />
          {t('list.filters')}
          {chips.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-blue-600 text-white text-xs font-semibold">
              {chips.length}
            </span>
          )}
          {panelOpen ? (
            <ChevronUpIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </button>
      )}
      {children}
      <div className="ml-auto inline-flex items-stretch rounded-xl border border-gray-300 bg-white overflow-hidden">
        <select
          value={sortValue}
          onChange={(e) => onSortChange(e.target.value)}
          aria-label={t('list.sortBy')}
          title={t('list.sortBy')}
          className="min-h-[44px] lg:min-h-0 lg:h-9 max-w-[11rem] pl-3 pr-7 py-0 text-sm bg-transparent border-0 text-gray-700 focus:ring-2 focus:ring-blue-500"
        >
          {sortOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t('list.sortPrefix', { label: opt.label })}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => onSortDirChange(sortDir === 'asc' ? 'desc' : 'asc')}
          className="min-w-[44px] lg:min-w-0 lg:w-9 inline-flex items-center justify-center px-2 border-l border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-800 transition-colors"
          title={sortDir === 'asc' ? t('list.ascending') : t('list.descending')}
          aria-label={sortDir === 'asc' ? t('list.ascending') : t('list.descending')}
        >
          {sortDir === 'asc' ? (
            <ArrowUpIcon className="w-4 h-4" />
          ) : (
            <ArrowDownIcon className="w-4 h-4" />
          )}
        </button>
      </div>
      {trailing}
      {chips.length > 0 && (
        <div className="w-full flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={chip.onClear}
              title={t('list.removeFilter')}
              className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-xs font-medium hover:bg-blue-100 transition-colors"
            >
              <span className="truncate max-w-[14rem]">{chip.label}</span>
              <XIcon className="w-3.5 h-3.5" />
            </button>
          ))}
          <button
            type="button"
            onClick={() => clearAllFilters(filters)}
            className="px-1.5 py-1 text-xs font-medium text-gray-500 hover:text-gray-800"
          >
            {t('list.clearAll')}
          </button>
        </div>
      )}
      {panelOpen && filters.length > 0 && (
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-3 border-t border-gray-100">
          {filters.map((filter, index) => (
            <FilterPanelControl
              key={(filter.type !== 'daterange' && filter.type !== 'multi' && filter.ariaLabel) || `filter-${index}`}
              filter={filter}
            />
          ))}
        </div>
      )}
    </MobileFilterBar>
  )
}
